import { Type } from "class-transformer";
import { flattenDeep } from "lodash-es";
import { BaseModel } from "../base.model";
import { User } from "../user/user.model";
import { AuditActionGroup } from "./audit-action-group.model";
import { AuditAction } from "./audit-action.model";
import { AuditObjectType } from "./audit-object-type.model";
import { AuditActionTypeEnum } from "./contract/audit-contract";

export class Audit extends BaseModel {
  id: number;
  @Type(() => User)
  user?: User;
  value?: string;
  createdById?: number;
  ip?: string;
  objectId?: string;
  @Type(() => AuditAction)
  auditAction?: AuditAction;
  actionId: number;
  @Type(() => AuditActionGroup)
  auditActionGroup?: AuditActionGroup;
  actionGroupId: number;
  @Type(() => AuditObjectType)
  objectType?: AuditObjectType;
  objectTypeId: number;
  userImpersonationId?: number;

  auditChanges: { field: string; oldValue: any; newValue: any }[] = [];
  flattenedValue: any = null;

  get isCreation() {
    if (!this.auditAction) {
      return false;
    }
    return !![
      AuditActionTypeEnum.Add,
      AuditActionTypeEnum.ImpersonationStarted,
      AuditActionTypeEnum.EmailSent,
      AuditActionTypeEnum.Login,
      AuditActionTypeEnum.WorkflowStart,
    ].includes(this.auditAction.id);
  }

  get isEdit() {
    if (!this.auditAction) {
      return false;
    }
    return this.auditAction.id === AuditActionTypeEnum.Edit;
  }

  get isDeletion() {
    if (!this.auditAction) {
      return false;
    }

    return !![
      AuditActionTypeEnum.Delete,
      AuditActionTypeEnum.ImpersonationEnded,
      AuditActionTypeEnum.ImpersonationExpired,
      AuditActionTypeEnum.WorkflowStop,
    ].includes(this.auditAction.id);
  }

  getAuditActionName(includeObjectName = false): string {
    let actionName = "Unknown Action";

    if (this.auditAction && this.auditAction.id && this.auditAction.name) {
      actionName = this.auditAction.name;

      if (
        ![
          AuditActionTypeEnum.ImpersonationStarted,
          AuditActionTypeEnum.ImpersonationEnded,
          AuditActionTypeEnum.EmailSent,
          AuditActionTypeEnum.ForgotPassword,
        ].includes(this.auditAction.id) &&
        includeObjectName &&
        this.objectType &&
        this.objectType.name
      ) {
        actionName = `${actionName} - ${this.objectType.name}`;
      }
    }

    return actionName;
  }

  get flattenAuditValue(): any {
    const formattedVal = this._parseLogValue(this.value || "");

    // If it's not an object or is null, return as-is
    if (!formattedVal || typeof formattedVal !== "object") {
      return formattedVal;
    }

    if (!this.flattenedValue) {
      // If it's an array, flatten array elements
      if (Array.isArray(formattedVal)) {
        this.flattenedValue = flattenDeep(formattedVal);
      }

      // If it's an object, flatten nested properties
      this.flattenedValue = this._flattenObject(formattedVal);
    }
    return this.flattenedValue;
  }

  /**
   * @description Recursively flatten a nested object into a single-level object with dot-notation keys
   * @param obj - The object to flatten
   * @param prefix - The prefix for nested keys (used internally for recursion)
   * @returns Flattened object
   */
  private _flattenObject(obj: any, prefix: string = ""): any {
    const flattened: any = {};

    Object.keys(obj).forEach((key) => {
      const value = obj[key];
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (value && typeof value === "object" && !Array.isArray(value)) {
        // Recursively flatten nested objects
        Object.assign(flattened, this._flattenObject(value, newKey));
      } else {
        // Add primitive values or arrays directly
        flattened[newKey] = value;
      }
    });

    return flattened;
  }

  private _parseLogValue(val: string) {
    try {
      let formattedVal = val;
      if (!formattedVal) return;

      // Step 1: remove extra escape characters if they exist
      if (typeof formattedVal === "string") {
        // Sometimes you may need to parse twice if it’s double-stringified
        formattedVal = JSON.parse(formattedVal);
        if (typeof formattedVal === "string") {
          formattedVal = JSON.parse(formattedVal);
        }
      }

      return formattedVal;
    } catch (e) {
      return null;
    }
  }
}
